/**
 * Datamodell för batterisimuleringen.
 *
 * Tidssteget är som default en timme. All energi i kWh, all effekt i kW,
 * alla priser i kr/kWh (öre/100). Tidstämplar är ISO-strängar i lokal tid
 * (Europe/Stockholm) eftersom Tibbers API returnerar det formatet.
 */

export type PriceArea = "SE1" | "SE2" | "SE3" | "SE4";

/** Ett tidssteg i timdatan. */
export interface HourPoint {
  /** ISO-tidstämpel för timmens start, t.ex. "2025-11-12T03:00:00+01:00". */
  from: string;
  /** Importerat från nätet under timmen (kWh). Alltid >= 0. */
  importKWh: number;
  /** Exporterat till nätet under timmen (kWh). Alltid >= 0. */
  exportKWh: number;
  /**
   * Spotpris under timmen (kr/kWh, exkl moms).
   * Detta är råpriset från Nordpool, inte Tibbers totalpris.
   * Sedan oktober 2025 är detta ett aritmetiskt medelvärde av de fyra
   * underliggande kvartpriserna — se quarterPrices för rådata.
   */
  spotPrice: number;
  /**
   * Underliggande kvartpriser för timmen (kr/kWh, 4 element från
   * 30 september 2025 och framåt). Modellen använder dem för att
   * beräkna intra-hour batteri-arbitrage utöver tim-baserad DP.
   * Pre-quarter historik: undefined eller saknas.
   */
  quarterPrices?: number[];
}

/** Batteriets fysiska och kommersiella konfiguration. */
export interface BatteryConfig {
  /** Total nominell kapacitet (kWh). */
  capacityKWh: number;
  /** Max effekt för laddning och urladdning (kW). */
  maxPowerKW: number;
  /**
   * Rundtursverkningsgrad (0-1). Typiskt 0.88-0.92 för moderna LFP-batterier.
   * Fördelas symmetriskt: ladd_eff = urladd_eff = sqrt(roundTripEfficiency).
   */
  roundTripEfficiency: number;
  /**
   * Minsta tillåtna SoC som andel av kapacitet (0-1).
   * Default 0.05 för LFP-cellivslängd.
   */
  minSocFraction: number;
  /** Investeringskostnad (kr) för återbetalningskalkyl. */
  investmentSek?: number;
}

/** Vilken styrlogik batteriet använder. */
export type Strategy =
  /** Grundläggande: ladda från solöverskott, urladda mot import. Ingen prisstyrning. */
  | "solar-only"
  /** Sol + ladda från nätet på dygnets billigaste timmar (perccentil-baserat). */
  | "solar-night-charge"
  /** Förutseende: 24h rullande optimering med kända priser och förbrukning. */
  | "predictive-24h"
  /** Teoretisk gräns: perfekt framförhållning hela perioden (LP-lösning). */
  | "optimal-foresight";

/**
 * Tilläggsintäkt från stödtjänster (FCR-D etc) via aggregator.
 *
 * Modellen tar hänsyn till att:
 *  - Marknaden betalar per kW maxeffekt, inte per kWh kapacitet
 *  - Priserna sjunker över tid när fler batterier ansluter sig
 *  - Aggregatorn tar en andel av intäkten (typiskt 20-40%)
 *  - Reserverad kapacitet inte kan användas till annan optimering
 */
export interface AncillaryServiceConfig {
  enabled: boolean;

  /**
   * Förväntad bruttointäkt per kW maxeffekt år 1 (kr/kW/år).
   * Detta är vad aggregatorn fakturerar Svenska kraftnät minus marknadsförluster,
   * INNAN aggregatorn tar sin andel. CheckWatts kundredovisning 2024:
   * median ~1850 kr/kW/år, fallande trend.
   */
  annualRevenuePerKW: number;

  /**
   * Aggregatorns andel (0-1). Typiskt 0.20-0.40.
   * Det användaren faktiskt får = annualRevenuePerKW * (1 - aggregatorShare).
   */
  aggregatorShare: number;

  /**
   * Årlig prisförändring (0.95 = -5%/år, 0.85 = -15%/år).
   * Reflekterar marknadsmättnad när fler batterier ansluter sig.
   */
  annualPriceMultiplier: number;

  /**
   * Tidshorisont vi räknar intäkten över (år). Default 5.
   * Bortom detta antas marknaden vara så mättad att intäkten är försumbar.
   */
  horizonYears: number;

  /**
   * Andel av batterikapaciteten som reserveras för FCR (0-1).
   * Typiskt 0.20-0.30. Reducerar effektivt kapacitet för andra besparingar.
   */
  reservedCapacityFraction: number;
}

/** Konfiguration för elnätsavgift. Stöder tre tariffmönster:
 *  - Mönster B (enkel): bara fixedMonthlySek + transmissionPerKWh
 *  - Mönster A (tröskelmodell, t.ex. E.on Syd): lägg till `tier`-objektet
 *  - Mönster C (effekttariff, t.ex. Ellevio): lägg till `powerTariff`-objektet
 *  - Kombination: kan kombineras fritt
 */
export interface GridFeeConfig {
  /** Identifierar tariff för UI-visning. */
  operator?: string;
  area?: string;
  fuseSize?: number;
  validFrom?: string;
  validTo?: string;
  sourceUrl?: string;

  /** Fast månadsavgift (kr/månad, exkl moms). */
  fixedMonthlySek: number;
  /** Överföringsavgift (kr/kWh, exkl moms). */
  transmissionPerKWh: number;

  /**
   * Tröskelmodell. När årlig import överstiger thresholdKWhPerYear gäller
   * `aboveTier`-värdena istället för `fixedMonthlySek`/`transmissionPerKWh`.
   * E.on Syd 16A: threshold 8000, lägre per-kWh men högre månadsavgift över.
   */
  tier?: {
    thresholdKWhPerYear: number;
    aboveTier: {
      fixedMonthlySek: number;
      transmissionPerKWh: number;
    };
  };

  /**
   * Effekttariff (kr/kW/månad baserat på topp-timmar).
   *
   * activateFromYear låter oss modellera EN FRAMTIDA effekttariff som ännu
   * inte är införd. Sätt 0 för aktiv från år 1, 2 för aktiv från år 2, osv.
   * Detta blir relevant från och med 2026 då Ei ska ta fram ny modell senast
   * 12 april 2027, men det är osäkert när enskilda nätbolag faktiskt inför.
   */
  powerTariff?: {
    pricePerKWPerMonth: number;
    topHoursAveraged: number; // 1 eller 3 vanligtvis
    seasonalDifferentiation?: {
      winterMonths: number[]; // [11, 12, 1, 2, 3]
      winterMultiplier: number; // 1.5x typiskt
    };
    /** År från idag då tariffen aktiveras. 0 = redan aktiv. */
    activateFromYear?: number;
  };

  /** Nätnytta-kompensation för utmatad el (kr/kWh). */
  feedInCompensationPerKWh: number;
}

/** Skattedelar av elpriset. */
export interface TaxConfig {
  /** Energiskatt (kr/kWh, exkl moms). 2026: 0.36 kr/kWh. */
  energyTaxPerKWh: number;
  /** Moms (0-1). Sverige: 0.25. */
  vatRate: number;
  /**
   * 60-öringen (skattereduktion för mikroproduktion).
   * Tas bort 2026-01-01, så detta värde gäller bara timmar före det datumet.
   */
  microproducerCreditPerKWh: number;
  /** Sista datum då 60-öringen gäller (inkl). Default 2025-12-31. */
  microproducerCreditEndDate: string;
}

/** Konfiguration för elhandel (Tibber). */
export interface SupplierConfig {
  /** Tibbers påslag på import (kr/kWh). */
  importMarkupPerKWh: number;
  /** Tibbers avdrag på försäljning, t.ex. 0 om de betalar rent spot. */
  exportMarkdownPerKWh: number;
  /** Eventuell extrabonus från elhandelsbolag för såld solel (kr/kWh). */
  exportBonusPerKWh: number;
}

/** Komplett scenariodefinition. */
/**
 * Antaganden om batteriets åldring över tid. Påverkar inte besparingen år 1
 * men används för att färgkoda återbetalningstiden mot förväntad livslängd
 * och för att visa kontext om hur lång payback ser ut.
 */
export interface BatteryDegradationConfig {
  /** Förväntad livslängd i år innan batteriet anses förbrukat. */
  expectedLifetimeYears: number;
  /**
   * Årlig kapacitetstapp som fraktion. Exempel: 0.02 = 2% per år. För LFP
   * ligger detta typiskt på 1.5-2.5% beroende på cykling och temperatur.
   */
  annualCapacityLossRate: number;
}

export interface Scenario {
  battery: BatteryConfig;
  strategy: Strategy;
  ancillary: AncillaryServiceConfig;
  grid: GridFeeConfig;
  tax: TaxConfig;
  supplier: SupplierConfig;
  /**
   * Antaganden om batteriets degradering över tid. Om ej satt används
   * neutrala defaultvärden vid presentation.
   */
  degradation?: BatteryDegradationConfig;
  /**
   * Planerade övriga elinvesteringar (isolering, kompletterande värme, elbil).
   * Modifierar timdata innan batterisimulering körs.
   */
  investmentPlans?: import("./investment-plans").InvestmentPlans;
}

/** Default när Scenario inte har explicit degradation. */
export const DEFAULT_DEGRADATION: BatteryDegradationConfig = {
  expectedLifetimeYears: 12,
  annualCapacityLossRate: 0.02,
};

/** En timme i resultatspåret. */
export interface ResultHour {
  from: string;
  /** Faktisk import efter att batteriet agerat (kWh). */
  importKWh: number;
  /** Faktisk export efter att batteriet agerat (kWh). */
  exportKWh: number;
  /** Hur mycket batteriet laddade från solen denna timme (kWh, AC-sida). */
  batteryChargeFromSolarKWh: number;
  /** Hur mycket batteriet laddade från nätet (kWh, AC-sida). */
  batteryChargeFromGridKWh: number;
  /** Hur mycket batteriet urladdade till huset (kWh, AC-sida). */
  batteryDischargeKWh: number;
  /** SoC i slutet av timmen (kWh). */
  socKWh: number;
  /** Nettokostnad denna timme (kr, inkl moms). Negativ = intäkt. */
  netCostSek: number;
}

/** Aggregerat resultat över hela perioden. */
export interface SimulationResult {
  scenario: Scenario;
  hours: ResultHour[];
  /** Period i ISO-datum (inkl). */
  periodFrom: string;
  periodTo: string;
  /** Annualiserade nyckeltal (skalat om perioden inte är exakt 365 dagar). */
  annual: {
    /** Nettokostnad utan batteri (kr/år, inkl moms). */
    baselineCostSek: number;
    /** Nettokostnad med batteri (kr/år, inkl moms). */
    batteryCostSek: number;
    /** Total besparing (kr/år). */
    savingsSek: number;
    /** Hur många kompletta cykler batteriet körde. */
    fullCycles: number;
    /** Andel av exporterad sol som istället egenanvändes (0-1). */
    selfConsumptionUplift: number;
    /** Intäkt från stödtjänster (kr/år). */
    ancillaryRevenueSek: number;
  };
  /** Återbetalningstid om investeringskostnad angiven (år), annars undefined. */
  paybackYears?: number;
}
